import { Link } from "react-router-dom";
import SEOMeta from "@/components/SEOMeta";
import NavbarNew from "@/components/NavbarNew";
import FooterNew from "@/components/FooterNew";
import { Skeleton } from "@/components/ui/skeleton";
import { ConceptBadge } from "@/components/shared/ConceptBadge";
import { useProjects, type Project } from "@/hooks/useProjects";
import { isConceptProject } from "@/lib/caseStudy";
import { LoadErrorState } from "@/components/shared/LoadErrorState";

function CaseGrid({ projects }: { projects: Project[] }) {
  return (
    <div className="mt-[42px] grid grid-cols-[repeat(auto-fit,minmax(min(320px,100%),1fr))] gap-5">
      {projects.map((project) => (
        <Link
          key={project.id}
          to={`/projetos/${project.id}`}
          className="block overflow-hidden rounded-[20px] border border-border bg-card shadow-card transition-[border-color,transform] duration-150 hover:-translate-y-0.5 hover:border-primary"
        >
          <div className="aspect-[16/10] overflow-hidden border-b border-border bg-card">
            {project.image_url ? (
              <img
                src={project.image_url}
                alt={project.title}
                loading="lazy"
                className="h-full w-full object-cover object-top"
              />
            ) : (
              <div className="h-full w-full [background-image:repeating-linear-gradient(135deg,hsl(var(--border))_0_8px,transparent_8px_16px)]" />
            )}
          </div>
          <div className="p-6">
            <div className="flex flex-wrap gap-2">
              {project.case_study?.sector && (
                <span className="inline-flex h-6 items-center rounded-[7px] bg-muted px-[11px] text-[11px] font-bold text-muted-foreground">
                  {project.case_study.sector}
                </span>
              )}
              {isConceptProject(project) && <ConceptBadge />}
            </div>
            <h3 className="mt-[14px] text-[19px] font-bold tracking-[-0.02em]">{project.title}</h3>
            <p className="mt-[9px] text-[14px] leading-[1.6] text-muted-foreground">
              {project.description}
            </p>
            {project.case_study?.metric && (
              <div className="mt-5 flex items-baseline gap-2.5 border-t border-border pt-4">
                <span className="text-2xl font-bold tracking-[-0.03em] text-primary">
                  {project.case_study.metric}
                </span>
                {project.case_study.metricLabel && (
                  <span className="text-[12.5px] text-muted-foreground">
                    {project.case_study.metricLabel}
                  </span>
                )}
              </div>
            )}
          </div>
        </Link>
      ))}
    </div>
  );
}

const Projects = () => {
  const { data: projects, isLoading, isError, refetch } = useProjects(true);
  const clientCases = (projects ?? []).filter((p) => !isConceptProject(p));
  const conceptCases = (projects ?? []).filter(isConceptProject);

  return (
    <div className="min-h-screen bg-background">
      <SEOMeta
        title="Cases | Eliveuton Dev"
        description="Projetos entregues, com o número que mudou no negócio do cliente. Conheça os cases de desenvolvimento e automação com IA."
        canonicalUrl="/projetos"
      />
      <NavbarNew />

      <main>
        <div className="mx-auto max-w-[1180px] px-8 pb-[90px] pt-[76px] max-[900px]:px-5">
          <h1 className="text-[44px] font-bold leading-[1.1] tracking-[-0.04em] max-[900px]:text-[38px]">
            Cases
          </h1>
          <p className="mt-[18px] max-w-[560px] text-[16.5px] text-muted-foreground">
            Projetos entregues, com o número que mudou no negócio do cliente.
          </p>

          {isLoading ? (
            <div className="mt-[42px] grid grid-cols-[repeat(auto-fit,minmax(320px,1fr))] gap-5">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="overflow-hidden rounded-[20px] border border-border bg-card"
                >
                  <Skeleton className="aspect-[16/10] rounded-none" />
                  <div className="space-y-3 p-6">
                    <Skeleton className="h-6 w-20" />
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : isError ? (
            <LoadErrorState title="Não foi possível carregar os cases." onRetry={() => refetch()} />
          ) : !projects || projects.length === 0 ? (
            <p className="mt-[42px] text-[14px] text-muted-foreground">
              Em breve novos cases serão adicionados.
            </p>
          ) : (
            <>
              {clientCases.length > 0 && <CaseGrid projects={clientCases} />}
              {conceptCases.length > 0 && (
                <section aria-labelledby="concept-heading" className="mt-[72px]">
                  <h2
                    id="concept-heading"
                    className="text-[31px] font-bold tracking-[-0.03em] max-[900px]:text-[27px]"
                  >
                    Projetos conceito
                  </h2>
                  <p className="mt-3.5 max-w-[620px] text-[15.5px] leading-[1.65] text-muted-foreground">
                    Marcas fictícias que criei para mostrar o tipo de site que posso fazer para o
                    seu negócio: identidade visual, landing page responsiva e pronta para receber
                    contatos. Abra a demo, navegue e imagine com a sua marca.
                  </p>
                  <CaseGrid projects={conceptCases} />
                </section>
              )}
            </>
          )}
        </div>
      </main>

      <FooterNew />
    </div>
  );
};

export default Projects;
